import React, { CSSProperties, PropsWithChildren, useEffect, useId, useState } from 'react'

type CollapsibleSectionProps = {
  label: string
  labelStyle?: CSSProperties
  disabled?: boolean
  /** Optional controlled state */
  isOpen?: boolean
  /** Initial open state when uncontrolled */
  defaultOpen?: boolean
  onToggle?: (nextOpen: boolean) => void
}

export function CollapsibleSection({
  label,
  labelStyle,
  disabled = false,
  isOpen,
  defaultOpen = false,
  onToggle,
  children,
}: PropsWithChildren<CollapsibleSectionProps>) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const contentId = useId()

  const controlled = isOpen !== undefined
  const open = controlled ? Boolean(isOpen) : internalOpen

  useEffect(() => {
    if (controlled) {
      setInternalOpen(Boolean(isOpen))
    }
  }, [controlled, isOpen])

  const handleToggle = () => {
    if (disabled) return

    const next = !open

    if (!controlled) {
      setInternalOpen(next)
    }

    onToggle?.(next)
  }

  return (
    <section style={{ width: '100%' }}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={open}
        aria-controls={contentId}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: '100%',
          fontSize: '16px',
          fontWeight: 600,
          marginTop: '1rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          color: disabled ? '#6b7280' : undefined,
          background: 'transparent',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          ...labelStyle,
        }}>
        <span aria-hidden>{open ? '▼' : '▶'}</span>
        <span>{label}</span>
      </button>

      {open ? <div id={contentId}>{children}</div> : null}
    </section>
  )
}
